import { useEffect, useState } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useActivateContext } from '@/hooks/useActivateContext';
import { onWrongContext, type WrongContextEvent } from '@/lib/wrongContext';

/**
 * Offers to switch context when the API refuses on the grounds that the caller
 * holds the permission somewhere else.
 *
 * Deliberately an offer, not an automatic switch. Changing context changes what
 * every other page in the app shows, so doing it silently because one request
 * failed would move the user's whole workspace as a side effect of a click they
 * made for another reason. `useActivateContext` invalidates every query on
 * success, so accepting re-runs the request that prompted this without any retry
 * machinery here.
 */
export function WrongContextPrompt() {
  const [event, setEvent] = useState<WrongContextEvent | null>(null);
  const activate = useActivateContext();

  useEffect(() => onWrongContext(setEvent), []);

  if (!event) return null;

  const dismiss = () => {
    setEvent(null);
  };

  const switchTo = (contextId: string) => {
    activate.mutate(contextId, { onSuccess: dismiss });
  };

  const single = event.grantingContexts.length === 1 ? event.grantingContexts[0] : null;

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="size-4" aria-hidden />
            Switch context?
          </AlertDialogTitle>
          {/* The backend sentence already names the permission and the
              contexts, and it is derived from the permission catalog — so it
              stays accurate without this component knowing anything about
              permissions. */}
          <AlertDialogDescription>{event.description}</AlertDialogDescription>
        </AlertDialogHeader>

        {/* More than one context can grant it; picking for the user would be a
            guess, so the choice is theirs. */}
        {!single && (
          <div className="flex flex-col gap-2">
            {event.grantingContexts.map((context) => (
              <Button
                key={context.id}
                variant="outline"
                className="justify-start"
                disabled={activate.isPending}
                onClick={() => {
                  switchTo(context.id);
                }}
              >
                {context.name}
              </Button>
            ))}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={dismiss}>Stay here</AlertDialogCancel>
          {single && (
            <AlertDialogAction disabled={activate.isPending} onClick={() => {
                switchTo(single.id);
              }}>
              {activate.isPending ? 'Switching…' : `Switch to ${single.name}`}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
